import type { SpecConstraintViewModel } from 'api/types/product-type/SpecConstraintViewModel';
import { SpecSelectionMode, SpecValueType } from 'api/types/product-type/SpecConstraintViewModel';
import type { SpecOperator } from 'types/listingFilterState';
import { normalizeSpecValueType } from 'utils/specConstraintUtils';
import { getSpecIcon } from 'api/utils/listingDetailMapper';

export type SpecPanelMode = 'filter' | 'create';

const NUMERIC_SPEC_OPTIONS: { value: SpecOperator; label: string }[] = [
  { value: '>=', label: 'Min' },
  { value: '<=', label: 'Max' },
  { value: '=', label: 'Tam' },
];

export interface SpecInlineFilterPanelProps {
  constraints: SpecConstraintViewModel[];
  mode: SpecPanelMode;
  specValues: Record<string, string>;
  specMultiValues: Record<string, string[]>;
  specOperators: Record<string, SpecOperator>;
  onSpecValueChange: (key: string, value: string) => void;
  onSpecMultiValueChange: (key: string, values: string[]) => void;
  onSpecOperatorChange: (key: string, operator: SpecOperator) => void;
  loading?: boolean;
  error?: string | null;
  emptyMessage?: string;
}

function isMultiSelect(constraint: SpecConstraintViewModel, mode: SpecPanelMode): boolean {
  if (mode === 'create') return false;
  const valueType = normalizeSpecValueType(constraint.valueType);
  // Browse filters: all enum specs use checkboxes with OR within same key (CR-015).
  if (valueType === SpecValueType.Enum) return true;
  return (constraint.selectionMode ?? SpecSelectionMode.Single) === SpecSelectionMode.Multi;
}

function toggleMultiValue(current: string[], value: string): string[] {
  return current.includes(value) ? current.filter((v) => v !== value) : [...current, value];
}

export default function SpecInlineFilterPanel({
  constraints,
  mode,
  specValues,
  specMultiValues,
  specOperators,
  onSpecValueChange,
  onSpecMultiValueChange,
  onSpecOperatorChange,
  loading = false,
  error = null,
  emptyMessage = 'Bu ürün tipi için tanımlı özellik filtresi yok.',
}: SpecInlineFilterPanelProps) {
  if (loading) {
    return (
      <p className="text-muted small mb-0" role="status">
        Özellikler yükleniyor…
      </p>
    );
  }

  if (error) {
    return (
      <div className="alert alert-warning py-2 small mb-0" role="alert">
        {error}
      </div>
    );
  }

  if (constraints.length === 0) {
    return <p className="text-muted small mb-0">{emptyMessage}</p>;
  }

  return (
    <div className="sb-spec-inline-panel d-flex flex-column gap-3">
      {constraints.map((constraint) => {
        const valueType = normalizeSpecValueType(constraint.valueType);
        const multi = isMultiSelect(constraint, mode);
        const selectedMulti = specMultiValues[constraint.key] ?? [];
        const selectedSingle = specValues[constraint.key] ?? '';

        return (
          <div key={constraint.id} className="sb-spec-inline-box border rounded p-2">
            <div className="sb-spec-inline-box__label small fw-semibold mb-2">
              <i className={`fas fa-${getSpecIcon(constraint.key)} text-primary me-2`} aria-hidden="true" />
              {constraint.displayName}
            </div>

            {valueType === SpecValueType.Number && (
              <div className="d-flex gap-2 align-items-center flex-wrap">
                {mode === 'filter' && (
                  <select
                    className="form-select form-select-sm"
                    style={{ maxWidth: '5.5rem' }}
                    aria-label={`${constraint.displayName} karşılaştırma`}
                    value={specOperators[constraint.key] ?? '='}
                    onChange={(e) => onSpecOperatorChange(constraint.key, e.target.value as SpecOperator)}
                  >
                    {NUMERIC_SPEC_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>
                        {opt.label}
                      </option>
                    ))}
                  </select>
                )}
                <input
                  type="number"
                  className="form-control form-control-sm sb-no-number-spinner"
                  style={{ minWidth: '4rem', maxWidth: '10rem' }}
                  value={selectedSingle}
                  onChange={(e) => onSpecValueChange(constraint.key, e.target.value)}
                  placeholder={mode === 'create' ? `${constraint.displayName} (opsiyonel)` : 'Değer'}
                />
              </div>
            )}

            {valueType === SpecValueType.String && (
              <input
                type="text"
                className="form-control form-control-sm"
                value={selectedSingle}
                onChange={(e) => onSpecValueChange(constraint.key, e.target.value)}
                placeholder={mode === 'create' ? `${constraint.displayName} (opsiyonel)` : 'Değer girin'}
              />
            )}

            {valueType === SpecValueType.Enum && (
              <div
                className="sb-spec-inline-options"
                role={multi ? 'group' : 'radiogroup'}
                aria-label={constraint.displayName}
              >
                {(constraint.allowedValues ?? []).map((av) => {
                  const isSelected = multi
                    ? selectedMulti.includes(av.value)
                    : selectedSingle === av.value;

                  return (
                    <label
                      key={av.id}
                      className={`sb-spec-inline-option${isSelected ? ' sb-spec-inline-option--selected' : ''}`}
                    >
                      <input
                        type={multi ? 'checkbox' : 'radio'}
                        name={multi ? undefined : `spec-${constraint.key}`}
                        className="form-check-input me-2 flex-shrink-0"
                        checked={isSelected}
                        onChange={() => {
                          if (multi) {
                            onSpecMultiValueChange(
                              constraint.key,
                              toggleMultiValue(selectedMulti, av.value)
                            );
                          } else {
                            onSpecValueChange(
                              constraint.key,
                              selectedSingle === av.value ? '' : av.value
                            );
                          }
                        }}
                      />
                      <span className="sb-spec-inline-option__text">{av.value}</span>
                    </label>
                  );
                })}
                {mode === 'filter' && !multi && selectedSingle && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mt-1 sb-spec-inline-clear"
                    onClick={() => onSpecValueChange(constraint.key, '')}
                  >
                    Temizle
                  </button>
                )}
                {mode === 'filter' && multi && selectedMulti.length > 0 && (
                  <button
                    type="button"
                    className="btn btn-link btn-sm p-0 mt-1 sb-spec-inline-clear"
                    onClick={() => onSpecMultiValueChange(constraint.key, [])}
                  >
                    Temizle
                  </button>
                )}
              </div>
            )}
          </div>
        );
      })}
    </div>
  );
}
